import { Dispatch } from 'react';

import { MARKET_DATA_TIME_ZONE } from '../../lib/constants/date-time.constants';
import { PredefinedMilisecondsTypeEnum } from '../../lib/enums';
import { CharacterEnum } from '../../lib/enums/character.enum';
import { Company } from '../../lib/models/company';
import { SymbolAndOptionalExchangeCode } from '../../lib/models/fundamental/types';
import { GWMarketDataQueryBucket, OHLCVDataRow } from '../../lib/models/gateway/types';
import store from '../../lib/store';
import { advancedChartSubscription } from '../../lib/store/market-data/subscriptionHelpers';
import MarketHoursState from '../../lib/store-util/MarketHoursState';
import TradePriceCache from '../../lib/store-util/TradePriceCache';

import type { Bar, DatafeedConfiguration, LibrarySymbolInfo, ResolutionString, Timezone } from '../../charting_library';

import { compareTradePriceAndOHLCV } from './helpers';

// Use it to keep a record of the most recent bar on the chart
const lastBarsCache = new Map();
const MAX_GATEWAY_DAY_HISTORY_YEARS = 15;
const MINUTE_BACKFILL_WINDOW_MS = PredefinedMilisecondsTypeEnum.oneDay * 14;
const HOUR_BACKFILL_WINDOW_MS = PredefinedMilisecondsTypeEnum.oneDay * 120;
const DAY_BACKFILL_WINDOW_MS = PredefinedMilisecondsTypeEnum.oneDay * 400;

export const getRequestedFromTime = (from?: number): number | undefined => (from != null ? from * 1000 : undefined);

export const needsOlderHistory = (data: OHLCVDataRow[], requestedFromTime?: number): boolean => {
  if (!data.length) {
    return true;
  }

  if (requestedFromTime == null) {
    return false;
  }

  return Number(data[0].dateAndTime) > requestedFromTime;
};

export const getBackfillEndTime = (fromDate: number | undefined, offsetMs: number): string | undefined => {
  if (fromDate == null) {
    return undefined;
  }

  return new Date(fromDate - offsetMs).toISOString();
};

export const getBackfillStartTime = ({
  fromDate,
  requestedFromTime,
  backfillWindowMs,
  minimumStartTime,
}: {
  fromDate?: number;
  requestedFromTime?: number;
  backfillWindowMs: number;
  minimumStartTime?: number;
}): string | undefined => {
  if (fromDate == null) {
    return undefined;
  }

  const contiguousStartTime = fromDate - backfillWindowMs;
  const boundedStartTime = requestedFromTime != null
    ? Math.max(requestedFromTime, contiguousStartTime)
    : contiguousStartTime;
  const effectiveStartTime = minimumStartTime != null
    ? Math.max(boundedStartTime, minimumStartTime)
    : boundedStartTime;

  return new Date(effectiveStartTime).toISOString();
};

const exportedObject = (
  chartMinuteData: OHLCVDataRow[],
  chartHourData: OHLCVDataRow[],
  chartDayData: OHLCVDataRow[],
  dispatch: Dispatch<any>,
  company?: Company,
) => {
  let minuteData = [...chartMinuteData];
  let hourData = [...chartHourData];
  let dayData = [...chartDayData];
  let lastDayFromDate = 0;
  let lastMinuteFromDate = 0;
  let lastHourFromData = 0;
  let interval: ReturnType<typeof setInterval>;
  const configurationData: DatafeedConfiguration = {
    // The `symbols_types` arguments are used for the `searchSymbols` method if a user selects this symbol type
    symbols_types: [{ name: 'Stock', value: 'stock' }],
    // supports_time: true,
    supports_marks: false,
    supports_timescale_marks: false,
    supported_resolutions: ['1', '5', '15', '30', '60', '1D', '1W', '1M'] as ResolutionString[],
  };

  return {
    onReady: (callback) => {
      setTimeout(() => callback(configurationData), 0);
    },

    // This feature is not going to be supported by 10n
    searchSymbols: async (userInput, exchange, symbolType, onResultReadyCallback) => {
      onResultReadyCallback([]);
    },

    resolveSymbol: (symbolName, onSymbolResolvedCallback, onResolveErrorCallback) => {
      if (!company) {
        onResolveErrorCallback('Cannot resolve symbol');
      } else {
        const { marketHours } = MarketHoursState.get(company.countryDesignation);
        const timezone = ((marketHours.timeZone as string) || MARKET_DATA_TIME_ZONE) as Timezone;
        const session = marketHours.open && marketHours.close
          ? `${marketHours.open.replace(CharacterEnum.Colon, CharacterEnum.Empty)}-${marketHours.close.replace(CharacterEnum.Colon, CharacterEnum.Empty)}`
          : '24x7';
        const symbolInfo: LibrarySymbolInfo = {
          data_status: 'streaming',
          description: company.companyName,
          exchange: company.exchange,
          format: 'price',
          full_name: company.companyName,
          has_daily: true,
          has_intraday: true,
          intraday_multipliers: ['1', '60'],
          listed_exchange: company.exchange,
          minmov: 1,
          name: company.symbol,
          pricescale: 100,
          delay: PredefinedMilisecondsTypeEnum.oneSecond,
          session,
          supported_resolutions: configurationData.supported_resolutions as ResolutionString[],
          timezone,
          type: company.issueType === 'etf' ? 'etf' : 'stock',
          volume_precision: 2,
          visible_plots_set: 'ohlcv',
        };
        setTimeout(() => {
          onSymbolResolvedCallback(symbolInfo);
        }, 0);
      }
    },

    getBars: (symbolInfo, resolution, periodParams, onHistoryCallback, onErrorCallback) => {
      if (!company) {
        return;
      }

      const gatewayState = store.getState().gateway.ohlcvData;
      const requestedFromTime = getRequestedFromTime(periodParams.from);
      const requestedToTime = getRequestedFromTime(periodParams.to);

      const mappedSymbol: SymbolAndOptionalExchangeCode = {
        symbol: symbolInfo.name,
        stockExchangeCode: company?.stockExchangeCode,
        countryDesignation: company?.countryDesignation,
      };
      const mappedSymbols: SymbolAndOptionalExchangeCode[] = [mappedSymbol];

      const { firstDataRequest } = periodParams;
      if (firstDataRequest) {
        minuteData = [...chartMinuteData];
        hourData = [...chartHourData];
        dayData = [...chartDayData];
      }

      if (gatewayState.minuteData.data.length > minuteData.length) {
        minuteData = [...gatewayState.minuteData.data];
      }
      if (gatewayState.hourData.data.length > hourData.length) {
        hourData = [...gatewayState.hourData.data];
      }
      if (gatewayState.dayData.data.length > dayData.length) {
        dayData = [...gatewayState.dayData.data];
      }

      if (['1', '5', '15', '30'].includes(resolution) && needsOlderHistory(minuteData, requestedFromTime)) {
        const fromDate = gatewayState.minuteData?.from;
        const startTime = getBackfillStartTime({
          fromDate,
          requestedFromTime,
          backfillWindowMs: MINUTE_BACKFILL_WINDOW_MS,
        });
        const endTime = getBackfillEndTime(fromDate, PredefinedMilisecondsTypeEnum.oneMinute);

        if (startTime && endTime && lastMinuteFromDate !== fromDate) {
          advancedChartSubscription(mappedSymbols, [GWMarketDataQueryBucket.OneMinute], startTime, endTime);
          lastMinuteFromDate = fromDate;
        }
        minuteData = [...gatewayState.minuteData.data];
      }

      if (resolution === '60' && needsOlderHistory(hourData, requestedFromTime)) {
        const fromDate = gatewayState.hourData?.from;
        const startTime = getBackfillStartTime({
          fromDate,
          requestedFromTime,
          backfillWindowMs: HOUR_BACKFILL_WINDOW_MS,
        });
        const endTime = getBackfillEndTime(fromDate, PredefinedMilisecondsTypeEnum.oneHour);

        if (gatewayState?.error && !hourData.length) {
          onHistoryCallback([], { noData: true });
        } else if (startTime && endTime && lastHourFromData !== fromDate) {
          advancedChartSubscription(mappedSymbols, [GWMarketDataQueryBucket.OneHour], startTime, endTime);
          lastHourFromData = fromDate;
        }
        hourData = [...gatewayState.hourData.data];
      }

      if (['1D', '1W', '1M'].includes(resolution) && needsOlderHistory(dayData, requestedFromTime)) {
        const fromDate = gatewayState.dayData?.from;
        const minimumStartDate = new Date();
        minimumStartDate.setFullYear(minimumStartDate.getFullYear() - MAX_GATEWAY_DAY_HISTORY_YEARS);
        const minimumStartTime = minimumStartDate.getTime();
        const startTime = getBackfillStartTime({
          fromDate,
          requestedFromTime,
          backfillWindowMs: DAY_BACKFILL_WINDOW_MS,
          minimumStartTime,
        });
        const endTime = getBackfillEndTime(fromDate, PredefinedMilisecondsTypeEnum.oneDay);

        if (!gatewayState?.error
          && startTime
          && endTime
          && fromDate != null
          && lastDayFromDate !== fromDate
          && fromDate > minimumStartTime) {
          advancedChartSubscription(mappedSymbols, [GWMarketDataQueryBucket.OneDay], startTime, endTime);
          lastDayFromDate = fromDate;
        }
        dayData = [...gatewayState.dayData.data];
      }

      let bars: Bar[] = [];
      let barData = dayData;
      if (resolution === '60') {
        barData = hourData;
      }
      if (['1', '5', '15', '30'].includes(resolution)) {
        barData = minuteData;
      }

      barData.forEach((bar: OHLCVDataRow) => {
        bars = [
          ...bars,
          {
            time: Number(bar.dateAndTime),
            low: bar.low,
            high: bar.high,
            open: bar.open,
            close: bar.close,
            volume: bar.volume,
          },
        ];
      });

      const barsInRange = bars.filter((bar) => {
        const isAfterFrom = requestedFromTime == null || bar.time >= requestedFromTime;
        const isBeforeTo = requestedToTime == null || bar.time <= requestedToTime;

        return isAfterFrom && isBeforeTo;
      });

      if (firstDataRequest) {
        lastBarsCache.set(symbolInfo.full_name, { ...bars[bars.length - 1] });
      }

      setTimeout(() => onHistoryCallback(barsInRange,
        { noData: !barsInRange.length }), PredefinedMilisecondsTypeEnum.twoHundredMilliseconds);
    },

    subscribeBars: (symbolInfo, resolution, onTick, subscriberUID, onResetCacheNeededCallback) => {
      const lastBar = lastBarsCache.get(symbolInfo.full_name);

      interval = setInterval(() => {
        const { currentPrice, tradeTime } = TradePriceCache.get(symbolInfo.name);

        if (tradeTime) {
          const tradeTimeUnix = new Date(tradeTime).getTime();
          const lastUpdate = compareTradePriceAndOHLCV(currentPrice, tradeTimeUnix, lastBar, resolution);
          const { open, high, low, close, time } = lastUpdate;

          onTick({ time, open, high, low, close });
          lastBarsCache.set(symbolInfo.full_name, { ...lastUpdate });
        }
      }, PredefinedMilisecondsTypeEnum.fiveSeconds);
    },

    unsubscribeBars: (subscriberUID) => {
      clearInterval(interval);
    },
  };
};
export default exportedObject;
